// CollapsedRail.js — the always-visible ~40px status rail.
import React from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { computeActiveSet } from 'agent/multiRecord';
import { T, pill, agentMode } from './liveTheme';
import LiveWave from './LiveWave';

const firstName = (name) => (name || '').trim().split(/\s+/)[0] || '?';
const initials = (name) => {
  const p = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
};
// Up to here: readable name chips on one line. Beyond: compact initials
// badges (no dot) that wrap to multiple lines so all of them stay legible.
const NAME_LIMIT = 4;

// Resolve an active conversationId to a display label — same rule as the
// timeline/tray (session name first, then the schedule slot's patient name).
const labelForId = (state, id) => {
  const session = (state.sessions || []).find(
    (s) => String(s.conversationId) === String(id),
  );
  if (session?.name) return session.name;
  for (const slot of state.schedule || []) {
    for (const p of slot.patients || []) {
      if (String(p.conversationId) === String(id)) {
        return p.patientName || 'Untitled visit';
      }
    }
  }
  return 'Untitled visit';
};

const CollapsedRail = () => {
  const { state, actions } = useComprehendLive();
  const multirecord = state.trayMode === 'multirecord';
  // MultiRecord++ drives recording off recordingDay + the computed active set
  // (the SAME source the tray and timeline use), NOT the ambient `s.recording`
  // flag — so the collapsed rail must read the active set here or it'll always
  // say STANDBY even mid-recording.
  const recording = multirecord
    ? state.recordingDay
      ? computeActiveSet(state.schedule, Date.now(), state.manualOverrides, {
          beforeMs: (state.bufferBeforeMin || 0) * 60000,
          afterMs: (state.bufferAfterMin || 0) * 60000,
          auto: state.autoSwitch,
        }).map((id) => ({
          conversationId: id,
          patientName: labelForId(state, id),
        }))
      : []
    : state.sessions.filter((s) => s.recording);
  const pendingCount = state.queue.filter((q) => q.status === 'pending').length;
  // Ambient uses the agent's status; MultiRecord shows a recording/standby
  // state tied to the recording day.
  const mode = multirecord
    ? state.recordingDay
      ? recording.length > 0
        ? {
            key: 'recording',
            label: 'RECORDING',
            color: T.red,
            pulse: true,
            big: true,
          }
        : {
            key: 'armed',
            label: state.autoSwitch ? 'AUTO-RECORD ON' : 'RECORDING DAY',
            color: state.autoSwitch ? T.amber : T.red,
            pulse: false,
          }
      : { key: 'standby', label: 'STANDBY', color: T.textFaint, pulse: false }
    : agentMode(state);
  // Few -> readable name chips; many -> compact initials badges that wrap.
  const useInitials = recording.length > NAME_LIMIT;

  return (
    <div
      onClick={actions.toggleExpanded}
      role="button"
      aria-label="Open Comprehend Live"
      style={{
        background: T.surface,
        padding: '7px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        borderBottom: `1px solid ${T.border}`,
        minHeight: 40,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <span
        className={mode.pulse ? 'cpl-dot' : undefined}
        style={{
          display: 'inline-block',
          width: mode.big ? 12 : 8,
          height: mode.big ? 12 : 8,
          borderRadius: '50%',
          background: mode.color,
          flexShrink: 0,
          transition: 'all .2s ease',
        }}
      />
      <span
        style={{
          color: mode.color,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '.7px',
          flexShrink: 0,
        }}
      >
        {mode.label}
      </span>

      <div
        style={{
          display: 'flex',
          gap: useInitials ? 4 : 6,
          flex: 1,
          minWidth: 0,
          alignItems: 'center',
          flexWrap: useInitials ? 'wrap' : 'nowrap',
          overflow: 'hidden',
        }}
      >
        {/* The recording wave — "I'm hearing you" — even when collapsed. */}
        {recording.length > 0 && <LiveWave />}

        {recording.length === 0 && (
          <span style={{ color: T.textFaint, fontSize: 11 }}>
            {multirecord && state.recordingDay
              ? state.autoSwitch
                ? 'No visit in the record window'
                : 'Tap a visit to record it'
              : 'No visits recording'}
          </span>
        )}

        {/* Few: name chips (dot + first name) on one line. */}
        {!useInitials &&
          recording.map((s) => (
            <span
              key={s.conversationId}
              onClick={(e) => {
                e.stopPropagation();
                actions.openDrillIn(s.conversationId);
              }}
              title={s.patientName}
              style={{
                ...pill('rgba(245,54,92,0.14)', T.redSoft),
                border: '1px solid rgba(245,54,92,0.4)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                cursor: 'pointer',
                minWidth: 0,
                flexShrink: 1,
                overflow: 'hidden',
              }}
            >
              <span
                className="cpl-dot"
                style={{
                  background: T.red,
                  width: 7,
                  height: 7,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  color: T.textPrimary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {firstName(s.patientName)}
              </span>
            </span>
          ))}

        {/* Many: compact initials badges (no dot), wrapping — all stay legible. */}
        {useInitials &&
          recording.map((s) => (
            <span
              key={s.conversationId}
              onClick={(e) => {
                e.stopPropagation();
                actions.openDrillIn(s.conversationId);
              }}
              title={s.patientName}
              style={{
                minWidth: 22,
                height: 20,
                padding: '0 5px',
                borderRadius: 6,
                background: 'rgba(245,54,92,0.16)',
                border: '1px solid rgba(245,54,92,0.45)',
                color: T.redSoft,
                fontSize: 10,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {initials(s.patientName)}
            </span>
          ))}
      </div>

      {pendingCount > 0 && (
        <span
          style={{
            ...pill('rgba(251,191,36,0.15)', T.amber),
            border: '1px solid rgba(251,191,36,0.45)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            flexShrink: 0,
          }}
        >
          <i
            className="fa fa-bolt"
            aria-hidden="true"
            style={{ fontSize: 10 }}
          />
          {pendingCount}
        </span>
      )}

      <span
        className={mode.key === 'listening' ? 'cpl-glow' : undefined}
        onClick={(e) => {
          e.stopPropagation();
          actions.setExpanded(true);
          actions.micDown();
        }}
        onMouseUp={() => actions.micUp()}
        title="Hold to talk to Comprehend"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: mode.key === 'listening' ? 30 : 26,
          height: mode.key === 'listening' ? 30 : 26,
          borderRadius: '50%',
          background:
            mode.key === 'listening' ? T.greenBright : 'rgba(45,206,137,0.18)',
          color: mode.key === 'listening' ? T.greenDark : T.greenBright,
          flexShrink: 0,
          transition: 'all .15s ease',
        }}
      >
        <i
          className="fa fa-microphone"
          aria-hidden="true"
          style={{ fontSize: 13 }}
        />
      </span>

      <i
        className={`fa fa-chevron-${state.expanded ? 'up' : 'down'}`}
        aria-hidden="true"
        style={{ color: T.textSecondary, fontSize: 12, flexShrink: 0 }}
      />
    </div>
  );
};

export default CollapsedRail;
